import { useEffect, useMemo, useState } from "react"

import { cn } from "@coval/ui/cn"

import type { ConversationClusteringFailureLensOutputV2 } from "@/types_api/backend/transport/get_latest_failure_pattern_run_output_response"

import { MAX_CLUSTER_FILTER_SIMULATION_OUTPUT_IDS } from "../hooks/useUploadedConversationsVisualizerClickForwarding"
import { resolveFailureTableIds } from "./uploadedConversationsFailureTableIds"

const percentageFormatter = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 1,
})

interface FailureGroupsProps {
  lens: ConversationClusteringFailureLensOutputV2
  onFilterSimulationOutputs?: (simulationOutputIds: string[]) => void
  onHighlightIssues: (issueIds: ReadonlySet<string> | null) => void
  seedKey: string
}

/** Shows the failure hierarchy and resolves group membership to table identities. */
export function UploadedConversationsVisualizerFailureGroups({
  lens,
  onFilterSimulationOutputs,
  onHighlightIssues,
  seedKey,
}: FailureGroupsProps) {
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null)
  const [expandedGroupIds, setExpandedGroupIds] = useState<Set<string>>(
    () => new Set(),
  )
  const tableIds = useMemo(() => resolveFailureTableIds(lens), [lens])
  const childById = useMemo(
    () => new Map(lens.children.map((child) => [child.group_id, child])),
    [lens.children],
  )

  useEffect(() => {
    setSelectedGroupId(null)
    setExpandedGroupIds(new Set())
    onHighlightIssues(null)
    return () => {
      onHighlightIssues(null)
    }
  }, [onHighlightIssues, seedKey])

  const toggleGroup = (groupId: string) => {
    setExpandedGroupIds((current) => {
      const next = new Set(current)
      if (next.has(groupId)) {
        next.delete(groupId)
      } else {
        next.add(groupId)
      }
      return next
    })
  }

  const selectGroup = (groupId: string, issueIds: string[]) => {
    const simulationOutputIds = Array.from(
      new Set(
        issueIds.flatMap((issueId) => {
          const simulationOutputId = tableIds.get(issueId)
          return simulationOutputId ? [simulationOutputId] : []
        }),
      ),
    )
    if (simulationOutputIds.length > MAX_CLUSTER_FILTER_SIMULATION_OUTPUT_IDS) {
      onFilterSimulationOutputs?.(simulationOutputIds)
      return
    }
    setSelectedGroupId(groupId)
    onHighlightIssues(new Set(issueIds))
    onFilterSimulationOutputs?.(simulationOutputIds)
  }

  return (
    <div
      className="flex h-full min-h-0 flex-col overflow-y-auto p-1"
      data-testid="uploaded-conversations-failure-groups"
    >
      <div className="mb-3 text-xs text-coval-text-muted">
        {lens.issue_count.toLocaleString()} issues across{" "}
        {lens.conversation_count.toLocaleString()} analyzed conversations
      </div>
      {lens.issue_count === 0 ? (
        <p
          className="text-sm text-coval-text-muted"
          data-testid="uploaded-conversations-failures-empty"
        >
          No failure issues were found in this run.
        </p>
      ) : (
        <ul className="space-y-3">
          {lens.parents.map((parent) => (
            <li
              key={parent.group_id}
              className="rounded-md border border-coval-border bg-surface-default p-2"
            >
              <div className="flex items-start gap-2">
                <button
                  type="button"
                  className="shrink-0 text-xs text-coval-text-muted focus-visible:outline-none"
                  aria-controls={`failure-children-${parent.group_id}`}
                  aria-expanded={expandedGroupIds.has(parent.group_id)}
                  data-testid={`uploaded-conversations-failure-expand-${parent.group_id}`}
                  onClick={() => toggleGroup(parent.group_id)}
                >
                  {expandedGroupIds.has(parent.group_id)
                    ? "Collapse"
                    : "Expand"}
                </button>
                <button
                  type="button"
                  className={cn(
                    "min-w-0 flex-1 rounded-sm text-left focus-visible:outline-none",
                    selectedGroupId === parent.group_id &&
                      "bg-surface-default-hover",
                  )}
                  aria-pressed={selectedGroupId === parent.group_id}
                  data-testid={`uploaded-conversations-failure-parent-${parent.group_id}`}
                  onClick={() => selectGroup(parent.group_id, parent.issue_ids)}
                >
                  <span className="block text-sm font-medium text-coval-text">
                    {parent.title}
                  </span>
                  <span className="block text-xs text-coval-text-muted">
                    {parent.description}
                  </span>
                  <span className="block text-xs text-coval-text-muted">
                    {parent.conversation_count} of {lens.conversation_count}{" "}
                    conversations (
                    {percentageFormatter.format(parent.conversation_percent)}%)
                    · {parent.issue_count} of {lens.issue_count} issues (
                    {percentageFormatter.format(parent.issue_percent)}%)
                  </span>
                </button>
              </div>
              {expandedGroupIds.has(parent.group_id) ? (
                <ul
                  id={`failure-children-${parent.group_id}`}
                  className="mt-2 space-y-1 border-l border-coval-border pl-3"
                >
                  {(parent.child_cluster_ids ?? []).map((childId) => {
                    const child = childById.get(childId)
                    if (!child) {
                      return null
                    }
                    return (
                      <li key={child.group_id}>
                        <button
                          type="button"
                          className={cn(
                            "w-full rounded-sm px-1 py-1 text-left hover:bg-surface-default-hover focus-visible:outline-none",
                            selectedGroupId === child.group_id &&
                              "bg-surface-default-hover",
                          )}
                          aria-pressed={selectedGroupId === child.group_id}
                          data-testid={`uploaded-conversations-failure-child-${child.group_id}`}
                          onClick={() =>
                            selectGroup(child.group_id, child.issue_ids)
                          }
                        >
                          <span className="block text-xs font-medium text-coval-text">
                            {child.title}
                          </span>
                          <span className="block text-xs text-coval-text-muted">
                            {child.description}
                          </span>
                          <span className="block text-xs text-coval-text-muted">
                            {child.conversation_count} of{" "}
                            {lens.conversation_count} conversations (
                            {percentageFormatter.format(
                              child.conversation_percent,
                            )}
                            %) · {child.issue_count} of {lens.issue_count}{" "}
                            issues (
                            {percentageFormatter.format(child.issue_percent)}%)
                          </span>
                        </button>
                      </li>
                    )
                  })}
                </ul>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
